export interface NameLabel {
  name: string;
  label: string;
}

export const mapToNameLabelArray = (map: Record<string, string>): NameLabel[] =>
  Object.keys(map).map(key => ({
    name: key,
    label: map[key],
  }));

// -----===[ MAPS ]===-----
// Maps represent a very simple, highly effective way to transform
// data of one form into data of another form. Maps are a very
// functional way to handle simple operations that might otherwise
// require much more complex code. Maps are often used in selectors,
// but may also be used in reductions or effects, or anywhere else
// that their simple behavior may apply.

export const INVOICE_STATUS_LABEL: Record<string, string> = {
  NEW: 'New',
  READ: 'Read',
  REJECTED: 'Declined',
  APPROVED_PENDING_PAYMENT: 'Approved',
  PAID_PENDING_LIEN_RELEASE: 'Pending',
  PAYMENT_PROCESSING: 'Processing',
  PAID: 'Paid',
  PAYOUT_FAILED: 'Payout failed',
};

export const INVOICE_STATUS_GROUP_MAP: Record<string, string> = {
  NEW: 'needs_review',
  READ: 'needs_review',
  REJECTED: 'rejected',
  APPROVED_PENDING_PAYMENT: 'ready_to_pay',
  // Paid-pending-lien-release is money already paid, held only for the lien
  // waiver signature — it's its own stage, not "Processing" (in-flight
  // payments). Bundling it into processing made the Processing total include
  // an invoice the list didn't render. The installer view already separates
  // it (ready_to_sign); this gives companies the same clarity.
  PAID_PENDING_LIEN_RELEASE: 'pending_lien_release',
  PAYMENT_PROCESSING: 'processing',
  PAID: 'settled',
  // TT-229: the payout to the installer failed and is being sorted out, so it
  // stays with the in-flight payments rather than disappearing from the list.
  PAYOUT_FAILED: 'processing',
};

export const INSTALLER_INVOICE_STATUS_GROUP_MAP: Record<string, string> = {
  NEW: 'sent',
  READ: 'sent',
  REJECTED: 'rejected',
  APPROVED_PENDING_PAYMENT: 'sent',
  PAID_PENDING_LIEN_RELEASE: 'ready_to_sign',
  PAYMENT_PROCESSING: 'processing',
  PAID: 'paid',
  PAYOUT_FAILED: 'processing',
};

export const INVOICE_STATUS_GROUP_LABEL_MAP: Record<string, string> = {
  needs_review: 'Needs Review',
  sent: 'Sent',
  ready_to_pay: 'Ready to Pay',
  ready_to_sign: 'Ready To Sign',
  processing: 'Processing',
  pending_lien_release: 'Pending Lien Release',
  settled: 'Settled',
  paid: 'Paid',
  rejected: 'Rejected',
};

export const INVOICE_STATUS_GROUP_ORDER_MAP: Record<string, number> = {
  needs_review: 0,
  ready_to_pay: 1,
  processing: 2,
  pending_lien_release: 3,
  settled: 4,
  rejected: 5,
  undefined: 99,
};

export const INSTALLER_INVOICE_STATUS_GROUP_ORDER_MAP: Record<string, number> = {
  sent: 0,
  ready_to_sign: 1,
  processing: 2,
  paid: 3,
  rejected: 4,
  undefined: 99,
};

export const ALL_INVOICE_STATUS_GROUP_ORDER_MAPS: Record<string, number> = {
  ...INVOICE_STATUS_GROUP_ORDER_MAP,
  ...INSTALLER_INVOICE_STATUS_GROUP_ORDER_MAP,
};

export const INVOICE_STATUS_GROUP_STATUS_MAP: Record<string, string[]> = {
  needs_review: ['NEW', 'READ'],
  ready_to_pay: ['APPROVED_PENDING_PAYMENT'],
  processing: ['PAYMENT_PROCESSING', 'PAYOUT_FAILED'],
  pending_lien_release: ['PAID_PENDING_LIEN_RELEASE'],
  settled: ['PAID'],
  rejected: ['REJECTED'],
};

export const INSTALLER_INVOICE_STATUS_GROUP_STATUS_MAP: Record<string, string[]> = {
  sent: ['NEW', 'READ', 'APPROVED_PENDING_PAYMENT'],
  ready_to_sign: ['PAID_PENDING_LIEN_RELEASE'],
  processing: ['PAYMENT_PROCESSING', 'PAYOUT_FAILED'],
  paid: ['PAID'],
  rejected: ['REJECTED'],
};

export const INVOICE_STATUS_GROUP_TO_COLOR: Record<string, string> = {
  needs_review: 'medium',
  sent: 'medium',
  ready_to_pay: 'gc-light-yellow',
  ready_to_sign: 'gc-yellow',
  processing: 'gc-green',
  pending_lien_release: 'gc-yellow',
  settled: 'dark',
  paid: 'gc-green',
  rejected: 'danger',
};

export const INVOICE_AREAS: Record<string, string> = {
  workOrder: 'work-order',
  adHoc: 'ad-hoc',
} as const;
